/**
 * What a technician actually carries to a job, and what they leave behind.
 *
 * This is the stock list for a small Kisii ISP, not a generic warehouse
 * catalogue. Everything here is something a GoSpeedNet technician either fits
 * at a client's premises or consumes getting there.
 *
 * Why it is a fixed list rather than free text: a drum of fibre that goes out
 * as "fibre", "Fiber 100m", "cable" and "drop wire" is four things no report
 * can add up, and unaccounted stock is the quietest way an ISP loses money.
 * The free-text box at the end of the form exists for the genuine exception,
 * and it is reviewed rather than counted.
 */
export const MATERIALS = [
    // ── Termination and distribution ────────────────────────────────────────
    {
        code: 'FAT',
        name: 'FAT box',
        aka: 'Fibre Access Terminal',
        category: 'termination',
        unit: 'each',
        maxPerVisit: 4,
    },
    {
        code: 'ATB',
        name: 'ATB',
        aka: 'Access Terminal Box',
        category: 'termination',
        unit: 'each',
        maxPerVisit: 6,
    },
    { code: 'SPLITTER', name: 'Splitter (1:8 / 1:16)', category: 'termination', unit: 'each', maxPerVisit: 8 },
    { code: 'JOINT_CLOSURE', name: 'Joint closure', aka: 'sleeve', category: 'termination', unit: 'each', maxPerVisit: 6 },
    { code: 'PATCH_PANEL', name: 'Patch panel', category: 'termination', unit: 'each', maxPerVisit: 4 },
    // ── Customer premises equipment ─────────────────────────────────────────
    { code: 'ONT', name: 'ONT', aka: 'fibre modem', category: 'cpe', unit: 'each', requiresSerial: true, maxPerVisit: 5 },
    { code: 'ROUTER', name: 'Router', category: 'cpe', unit: 'each', requiresSerial: true, maxPerVisit: 5 },
    { code: 'ACCESS_POINT', name: 'Access point / extender', category: 'cpe', unit: 'each', requiresSerial: true, maxPerVisit: 5 },
    { code: 'CPE_ANTENNA', name: 'Wireless CPE / antenna', category: 'cpe', unit: 'each', requiresSerial: true, maxPerVisit: 4 },
    // ── Active network gear ─────────────────────────────────────────────────
    { code: 'SWITCH', name: 'Switch', category: 'active', unit: 'each', requiresSerial: true, maxPerVisit: 4 },
    { code: 'MEDIA_CONVERTER', name: 'Media converter', category: 'active', unit: 'each', requiresSerial: true, maxPerVisit: 4 },
    { code: 'SFP', name: 'SFP module', category: 'active', unit: 'each', maxPerVisit: 8 },
    // ── Cable, measured rather than counted ─────────────────────────────────
    //
    // These are the reason the form asks for a distance. A drum is bought by the
    // metre and billed by the metre, and "1 fibre" on a job sheet reconciles
    // against nothing.
    { code: 'FIBRE_DROP', name: 'Drop fibre', aka: 'drop wire', category: 'cable', unit: 'metre', maxPerVisit: 2000 },
    { code: 'FIBRE_ADSS', name: 'ADSS aerial fibre', category: 'cable', unit: 'metre', maxPerVisit: 5000 },
    { code: 'FIBRE_ARMOURED', name: 'Armoured fibre', category: 'cable', unit: 'metre', maxPerVisit: 5000 },
    { code: 'CAT6', name: 'Cat 6 cable', category: 'cable', unit: 'metre', maxPerVisit: 500 },
    { code: 'CAT5E', name: 'Cat 5e cable', category: 'cable', unit: 'metre', maxPerVisit: 500 },
    { code: 'PATCH_CORD', name: 'Patch cord', category: 'cable', unit: 'each', maxPerVisit: 20 },
    { code: 'PIGTAIL', name: 'Pigtail', category: 'cable', unit: 'each', maxPerVisit: 24 },
    // ── Power and mounting ──────────────────────────────────────────────────
    { code: 'POE_INJECTOR', name: 'PoE injector', category: 'power', unit: 'each', maxPerVisit: 5 },
    { code: 'POWER_ADAPTER', name: 'Power adapter', category: 'power', unit: 'each', maxPerVisit: 5 },
    { code: 'UPS', name: 'UPS / backup battery', category: 'power', unit: 'each', requiresSerial: true, maxPerVisit: 2 },
    { code: 'POLE', name: 'Pole', category: 'power', unit: 'each', maxPerVisit: 20 },
    { code: 'WALL_MOUNT', name: 'Wall mount / bracket', category: 'power', unit: 'each', maxPerVisit: 10 },
    // ── Consumables ─────────────────────────────────────────────────────────
    { code: 'CLAMP', name: 'Clamp / tension set', category: 'consumable', unit: 'each', maxPerVisit: 50 },
    { code: 'CABLE_TIE', name: 'Cable ties', category: 'consumable', unit: 'each', maxPerVisit: 200 },
    { code: 'TRUNKING', name: 'Trunking / conduit', category: 'consumable', unit: 'metre', maxPerVisit: 200 },
    { code: 'SPLICE_PROTECTOR', name: 'Splice protector', category: 'consumable', unit: 'each', maxPerVisit: 48 },
    { code: 'RJ45', name: 'RJ45 connector', category: 'consumable', unit: 'each', maxPerVisit: 100 },
    { code: 'FASTCONNECT', name: 'Fast connector', category: 'consumable', unit: 'each', maxPerVisit: 24 },
];
export const MATERIAL_BY_CODE = new Map(MATERIALS.map((m) => [m.code, m]));
export const CATEGORY_LABEL = {
    termination: 'Termination & distribution',
    cpe: 'Customer equipment',
    active: 'Active gear',
    cable: 'Cable (give the distance)',
    aerial: 'Aerial hardware',
    ducting: 'Underground & ducting',
    splicing: 'Splicing & jointing',
    test: 'Test equipment',
    tooling: 'Tools',
    safety: 'Safety equipment',
    power: 'Power & mounting',
    consumable: 'Consumables',
};
/**
 * What belongs in each category, in the words somebody adding an item would
 * use.
 *
 * Shown on the form. A dropdown of twelve nouns is a quiz if the difference
 * between "Termination" and "Splicing" is only obvious to whoever wrote the
 * list — and the cost of a wrong guess is a report that quietly counts a
 * closure as a consumable for a year.
 */
export const CATEGORY_HELP = {
    termination: 'Where the fibre ends or splits — FAT boxes, ATBs, splitters, joint closures.',
    cpe: 'What is left at the customer: ONT, router, their power adapter.',
    active: 'Anything powered in our own network — switches, media converters, injectors.',
    cable: 'Sold by length. Drop wire, ADSS, patch leads, earth wire.',
    aerial: 'Getting the cable through the air — poles, stays, clamps, brackets, suspension.',
    ducting: 'Getting it underground — conduit, duct rods, draw rope, chambers, warning tape.',
    splicing: 'Consumed while jointing — pigtails, sleeves, protectors, fusion supplies.',
    test: 'Instruments a technician signs out and brings back — OTDR, power meter, VFL.',
    tooling: 'Tools issued to a van — strippers, cleavers, ladders, crimpers.',
    safety: 'Worn or used to work safely — harness, helmet, gloves, cones, signage.',
    power: 'Keeping it running and holding it up — PSUs, batteries, trunking, fixings.',
    consumable: 'Small things used up and not tracked individually — ties, tape, labels.',
};
export const MATERIAL_CATEGORIES = Object.keys(CATEGORY_LABEL);
/**
 * Whether a string names a category.
 *
 * Exists because a category arriving from a form is a string, and the item it
 * lands on is grouped by it on every screen. An unrecognised one would render
 * its own heading of one item, which reads as a bug in the grouping rather
 * than as bad input.
 */
/**
 * The whole stock list as a lookup: built-ins, plus an organisation's own.
 *
 * Exists because widening *validation* to accept custom items was not enough.
 * The write paths resolved each line's spec from `MATERIAL_BY_CODE` directly
 * and asserted the result was there with `!` — so a custom item passed
 * validation and then crashed reading `.code` of undefined. The assertion is
 * what hid it: the map genuinely does not contain custom codes, and the type
 * said otherwise.
 *
 * One helper, used by the validator and by both writers, so "what is on the
 * list" has a single answer.
 */
export function materialLookup(extra = []) {
    if (extra.length === 0)
        return MATERIAL_BY_CODE;
    /*
     * The caller's list wins, and that inversion matters.
     *
     * Built-ins used to be listed last so they beat anything passed in, on the
     * reasoning that a custom item must never shadow one. That reasoning is
     * obsolete: the built-ins are rows in each organisation's own list now, and
     * `extra` *is* that list — including the built-ins as edited. Letting the
     * constants win would mean a business that renamed "FAT box" saw its new
     * name on every screen and the old one here, in the validator, which is the
     * one place a disagreement produces a refusal nobody can explain.
     *
     * MATERIAL_BY_CODE stays as the floor for callers that pass nothing: a test,
     * or the phone before it has fetched the catalogue.
     */
    return new Map([...MATERIAL_BY_CODE, ...extra.map((one) => [one.code, one])]);
}
/**
 * The stored key for a category somebody has just named.
 *
 * "Aerial hardware" becomes `aerial_hardware`. Nobody types this: it is what
 * items are filed under, it never changes once the category exists, and it is
 * the reason renaming a category does not re-label a year of job sheets.
 *
 * Derived from the label rather than asked for, because a person adding
 * "Splicing & jointing" at a cabinet has no opinion about identifiers and
 * should not be made to acquire one.
 */
export function categoryKeyFrom(label) {
    return label
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 40);
}
/** Whether a key is one this system will store. */
export function isCategoryKey(value) {
    return typeof value === 'string' && /^[a-z0-9]+(_[a-z0-9]+)*$/.test(value) && value.length <= 40;
}
/**
 * The categories every organisation starts with.
 *
 * This is now a *seed*, not the list. The table is the list — see
 * `InventoryCategory` — and these are what it is populated with the first time
 * an organisation opens the stock list. Kept here rather than in a migration
 * so an organisation created next year gets them too.
 */
export const BUILT_IN_CATEGORIES = MATERIAL_CATEGORIES.map((key, index) => ({
    key,
    label: CATEGORY_LABEL[key],
    help: CATEGORY_HELP[key],
    sortOrder: (index + 1) * 10,
}));
export function isMaterialCategory(value) {
    return typeof value === 'string' && MATERIAL_CATEGORIES.includes(value);
}
/**
 * Checks what came off the phone before it is stored.
 *
 * Strict on purpose. Stock that is recorded loosely is stock that walks: a
 * router with no serial cannot be traced to the job it left on, and a drum of
 * fibre logged as "1" reconciles against nothing at month end. Every rejection
 * here is a line an accountant would otherwise have to chase.
 */
export function validateMaterials(lines, 
/**
 * Items this organisation added to its own list, on top of the built-in set.
 *
 * Passed in rather than looked up, because this function is pure and is the
 * same rule on the phone and on the server. A caller that has no custom
 * items — a test, or the phone before it has fetched the catalogue — passes
 * nothing and gets exactly the behaviour it had before.
 *
 * This list wins over the built-in constants, because it *is* the built-in
 * list as this organisation has it — the seeded rows, with whatever has been
 * corrected since. One code still means one thing: the unique index on
 * (orgId, code) sees to that.
 */
extra = []) {
    const errors = [];
    const seen = new Set();
    const cleaned = [];
    const catalogue = materialLookup(extra);
    for (const line of lines) {
        const spec = catalogue.get(line.code);
        if (!spec) {
            errors.push({ code: line.code, message: `"${line.code}" is not on the stock list.` });
            continue;
        }
        // Two lines for the same item would let a quantity be quietly doubled or,
        // worse, halved past a reviewer's eye.
        if (seen.has(line.code)) {
            errors.push({ code: line.code, message: `${spec.name} is listed twice. Combine them.` });
            continue;
        }
        seen.add(line.code);
        if (!Number.isFinite(line.quantity) || line.quantity <= 0) {
            errors.push({
                code: line.code,
                message: spec.unit === 'metre'
                    ? `How many metres of ${spec.name} were used?`
                    : `How many ${spec.name} were used?`,
            });
            continue;
        }
        // Cable is measured; everything else is counted. Half a router is a typo.
        if (spec.unit === 'each' && !Number.isInteger(line.quantity)) {
            errors.push({ code: line.code, message: `${spec.name} has to be a whole number.` });
            continue;
        }
        if (spec.maxPerVisit && line.quantity > spec.maxPerVisit) {
            errors.push({
                code: line.code,
                message: `${line.quantity}${spec.unit === 'metre' ? 'm' : ''} of ${spec.name} in one visit ` +
                    `looks like a typo. The most expected is ${spec.maxPerVisit}${spec.unit === 'metre' ? 'm' : ''}.`,
            });
            continue;
        }
        const serial = line.serial?.trim();
        if (spec.requiresSerial && !serial) {
            errors.push({
                code: line.code,
                message: `${spec.name} needs its serial number — that is how it is traced if it goes missing.`,
            });
            continue;
        }
        if (serial && serial.length < 4) {
            errors.push({ code: line.code, message: `That serial for ${spec.name} looks too short.` });
            continue;
        }
        // One serial covers one unit. Two routers on one line cannot both be traced.
        if (spec.requiresSerial && line.quantity > 1 && serial) {
            const serials = serial.split(/[,\s]+/).filter(Boolean);
            if (serials.length !== line.quantity) {
                errors.push({
                    code: line.code,
                    message: `${line.quantity} × ${spec.name} needs ${line.quantity} serial numbers, separated by commas.`,
                });
                continue;
            }
        }
        cleaned.push({
            code: line.code,
            quantity: line.quantity,
            ...(serial ? { serial } : {}),
            ...(line.note?.trim() ? { note: line.note.trim() } : {}),
        });
    }
    return errors.length > 0 ? { ok: false, errors } : { ok: true, value: cleaned };
}
/** A one-line summary for the ticket timeline and the dashboard. */
export function summariseMaterials(lines) {
    if (lines.length === 0)
        return 'Nothing used';
    return lines
        .map((line) => {
        const spec = MATERIAL_BY_CODE.get(line.code);
        if (!spec)
            return `${line.quantity} × ${line.code}`;
        return spec.unit === 'metre'
            ? `${line.quantity}m ${spec.name}`
            : `${line.quantity} × ${spec.name}`;
    })
        .join(', ');
}
//# sourceMappingURL=materials.js.map